import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {ActivityLogPreviewBox} from '../control_central/ActivityLogPreviewBox';
import {ActivityLogCommentModal} from '../activity_log/ActivityLogCommentModal';
import './RailCards.scss';

// Capas del digest: TODAS. El colapso de ráfagas server-side evita que 5 ticks de la misma alerta
// se coman el widget (el problema del widget legacy).
//
// Copiada de `ControlCentralContent` a propósito, NO importada: la ficha clásica no se toca en esta
// etapa y un import cruzado ataría el Panel a un componente que algún día se borra. Son 13 strings.
const TIMELINE_CATEGORIES = 'PLATFORM_CHANGE,EXTERNAL,MEMORY_OPS,BUDGET,CONFIGURATION,STATUS,'
    + 'ACCOUNTS,INTEGRATIONS,REPORTS,AGENT,ALERTS,COMMENT,TEAM';

const PAGE_SIZE = 4;

/**
 * Card de la Bitácora del rail: las últimas cosas que pasaron con este cliente.
 *
 * Es la ÚNICA card del Panel que no sale del agregado — tiene su propio endpoint. Es a propósito:
 * si `GET client/<id>/panel/` se cae, el rail sigue contando la historia del cliente. Por lo mismo
 * su error es local (la card muestra "reintentar") y no toca al resto de la pantalla.
 *
 * El cuerpo es `ActivityLogPreviewBox` tal cual: mismo componente que la ficha clásica, así la
 * bitácora se ve y se comporta igual en las dos pantallas (título, empty state, "Agregar nota" y el
 * "Ver bitácora completa" que ya apunta al destino de ficha 2.1).
 */
export function BitacoraCard({clientId, businessUnits = []}) {
    const {t} = useTranslation();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [reloadToken, setReloadToken] = useState(0);

    const refetch = useCallback(() => setReloadToken((token) => token + 1), []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);

        const url = `${API_URL}client/${clientId}/activity-log/?page_size=${PAGE_SIZE}`
            + `&categories=${TIMELINE_CATEGORIES}&tz_offset=${new Date().getTimezoneOffset()}`;
        new APIClient(url).call('GET').then((data) => {
            if (cancelled) return;
            if (data && data.status === 'OK') {
                setEntries((data.data && data.data.entries) || []);
                setFailed(false);
            } else {
                setEntries([]);
                setFailed(true);
            }
            setLoading(false);
        });

        return () => {
            cancelled = true;
        };
    }, [clientId, reloadToken]);

    const handleSubmitNote = (commentText, buId) => {
        setSubmitting(true);
        const body = {comment_text: commentText};
        if (buId) body.business_unit_id = buId;

        new APIClient(`${API_URL}client/${clientId}/activity-log/`).call('POST', body).then((data) => {
            setSubmitting(false);
            if (data && data.status === 'OK') {
                setShowModal(false);
                toast.success(t('client_panel.bitacora_note_saved', 'Nota agregada a la bitácora'));
                refetch();
            } else {
                // Nada de alert(): el modal queda abierto con el texto escrito para reintentar.
                toast.error(t('client_panel.bitacora_note_error', 'No pudimos guardar la nota'));
            }
        });
    };

    if (failed) {
        return (
            <div className="box pnl-card">
                <div className="pnl-card__head">
                    <h2 className="pnl-card__title">{t('client_panel.bitacora_title', 'Bitácora')}</h2>
                </div>
                <div className="pnl-cnt pnl-rail-error">
                    <p>{t('client_panel.bitacora_error', 'No se pudo cargar la bitácora')}</p>
                    <button type="button" className="pnl-btn pnl-btn--ghost" onClick={refetch}>
                        {t('client_panel.bitacora_retry', 'Reintentar')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <ActivityLogPreviewBox
                clientId={clientId}
                entries={entries}
                loading={loading}
                onAddNote={() => setShowModal(true)}
                fichaV2={true}
            />

            <ActivityLogCommentModal
                isOpen={showModal}
                toggle={() => setShowModal(false)}
                businessUnits={businessUnits}
                onSubmit={handleSubmitNote}
                isSubmitting={submitting}
            />
        </>
    );
}
